'use client'

import * as React from 'react'
import * as SliderPrimitive from '@radix-ui/react-slider'

import { cn } from '../../../lib/utils'
import MetafieldLabel from '../MetafieldLabel'

import s from '../../styles/animations.module.css'

export type SliderProps = {
  id: string
  helpText?: string
  className?: string
  errorMessage?: string
  metafield?: {
    label: string
    href: string
  }
  handleChange?: (value: number[]) => void | Promise<void>
  label?: string
  value: number[]
  defaultValue: number[]
  min: number
  max: number
  step: number
}

const SliderComp = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root>
>(({ className, ...props }, ref) => {
  const [isDragging, setIsDragging] = React.useState(false)

  return (
    <SliderPrimitive.Root
      ref={ref}
      className={cn(
        'relative flex w-full touch-none select-none items-center',
        className,
      )}
      {...props}
      onValueChange={(value) => {
        setIsDragging(true)
        props.onValueChange && props.onValueChange(value)
      }}
      onValueCommit={(value) => {
        setIsDragging(false)
        props.onValueCommit && props.onValueCommit(value)
      }}
    >
      <SliderPrimitive.Track className="relative h-1.5 w-full grow overflow-hidden rounded-full bg-primary/20">
        <SliderPrimitive.Range className="absolute h-full bg-primary" />
      </SliderPrimitive.Track>
      {props.value?.map((v, index) => (
        <SliderPrimitive.Thumb
          key={index}
          className="block h-4 w-4 rounded-full border border-primary/50 bg-background shadow transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
        >
          {isDragging && (
            <div
              className={cn(
                'absolute -top-4 -left-6 w-16 h-6 text-xs text-center text-primary/50',
              )}
            >
              {v}
            </div>
          )}
        </SliderPrimitive.Thumb>
      ))}
    </SliderPrimitive.Root>
  )
})
SliderComp.displayName = SliderPrimitive.Root.displayName

const Slider = (props: SliderProps) => {
  const {
    label,
    className,
    metafield,
    errorMessage,
    id,
    helpText,
    handleChange,
    value,
    defaultValue,
    min,
    max,
    step,
  } = props

  const [inputValue, setInputValue] = React.useState<number[]>(value || [])

  const triggerChange = async (value: number[]) => {
    let newValue = value

    if (value[1] && value[0] && value[0] > value[1]) {
      newValue = value.reverse()
    }

    // dispatch if exists
    handleChange && (await handleChange(newValue))
  }

  return (
    <div>
      <label
        className={cn({
          'text-destructive opacity-100 leading-tight w-full': errorMessage,
        })}
      >
        <div
          className={cn(
            'flex justify-between items-center text-muted-foreground mb-2',
            className,
          )}
        >
          {label}
          {metafield && <MetafieldLabel {...metafield} />}
        </div>
        <SliderComp
          className="w-full"
          defaultValue={defaultValue}
          min={min}
          max={max}
          step={step}
          onValueChange={setInputValue}
          onValueCommit={triggerChange}
          value={inputValue}
        />
        {helpText && (
          <div className="text-muted-foreground normal-case text-xs mt-2">
            {helpText}
          </div>
        )}
        {errorMessage && (
          <div
            title={`input-error-${id}`}
            className={cn('text-destructive text-xs', s.smallTransitionIn)}
          >
            {errorMessage}
          </div>
        )}
      </label>
    </div>
  )
}

export { Slider }
